import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";

type Column = {
  id?: string | number;
  column_key?: string;
  display_label?: string;
  column_kind?: string;
  is_visible?: boolean;
};

type ColumnConfigurationModalProps = {
  show: boolean;
  onHide?: () => void;
  columns?: Column[];
  onSave?: (columns: Column[]) => Promise<boolean | void> | boolean | void;
  mode?: "all" | "enrichment";
};

const normalizeGroupKey = (groupKey?: string) => String(groupKey || "other").toLowerCase();
const formatGroupLabel = (groupKey?: string) => normalizeGroupKey(groupKey).replace(/_/g, " ").toUpperCase();

const FIXED_COLUMN_KEYS = new Set(["company_name", "country"]);
const isFixedColumn = (column?: Column) => FIXED_COLUMN_KEYS.has(column?.column_key ?? "");

const groupColumns = (columns: Column[] = []) => {
  const groupedMap = (Array.isArray(columns) ? columns : []).reduce<Record<string, Column[]>>((acc, column) => {
    const key = normalizeGroupKey(column?.column_kind || "trade_data");
    if (!acc[key]) acc[key] = [];
    acc[key].push(column);
    return acc;
  }, {});

  return Object.entries(groupedMap)
    .map(([key, items]) => ({
      key,
      label: formatGroupLabel(key),
      items: [...items].sort((a, b) => String(a?.display_label || a?.column_key || "").localeCompare(String(b?.display_label || b?.column_key || ""))),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const ColumnConfigurationModal = ({ show, onHide, columns = [], onSave, mode = "all" }: ColumnConfigurationModalProps) => {
  const [workingColumns, setWorkingColumns] = useState<Column[]>([]);
  const [draggedColumnId, setDraggedColumnId] = useState<string | number | null>(null);
  const isEnrichmentOnlyMode = mode === "enrichment";

  useEffect(() => {
    if (!show) return;
    setWorkingColumns(
      (Array.isArray(columns) ? columns : [])
        .filter((column) => (isEnrichmentOnlyMode ? column?.column_kind === "enrichment" : true))
        .map((column) => ({
          ...column,
          is_visible: isFixedColumn(column) ? true : column?.is_visible !== false,
        }))
    );
  }, [show, columns, isEnrichmentOnlyMode]);

  const fixedColumns = useMemo(() => workingColumns.filter(isFixedColumn), [workingColumns]);
  const configurableColumns = useMemo(() => workingColumns.filter((col) => !isFixedColumn(col)), [workingColumns]);
  const groupedColumns = useMemo(() => groupColumns(configurableColumns), [configurableColumns]);
  const selectedColumns = useMemo(() => configurableColumns.filter((column) => column?.is_visible), [configurableColumns]);
  const totalSelected = useMemo(() => workingColumns.filter((c) => c?.is_visible).length, [workingColumns]);

  const toggleColumn = (columnId: string | number | undefined, checked: boolean) => {
    setWorkingColumns((prev) =>
      prev.map((column) => {
        if (column?.id !== columnId) return column;
        if (isFixedColumn(column)) return column;
        return { ...column, is_visible: checked };
      })
    );
  };

  const handleGroupAddAll = (groupKey: string) => {
    setWorkingColumns((prev) =>
      prev.map((column) => {
        if (normalizeGroupKey(column?.column_kind || "trade_data") !== groupKey) return column;
        return { ...column, is_visible: true };
      })
    );
  };

  const handleSave = async () => {
    const didSave = await onSave?.(workingColumns);
    if (didSave === false) return;
    onHide?.();
  };

  const reorderSelectedColumns = (sourceColumnId: string | number | null, targetColumnId: string | number | null) => {
    if (!sourceColumnId || !targetColumnId || sourceColumnId === targetColumnId) return;
    const visibleConfigurable = configurableColumns.filter((column) => column?.is_visible);
    const hiddenConfigurable = configurableColumns.filter((column) => !column?.is_visible);
    const sourceIndex = visibleConfigurable.findIndex((column) => column?.id === sourceColumnId);
    const targetIndex = visibleConfigurable.findIndex((column) => column?.id === targetColumnId);
    if (sourceIndex === -1 || targetIndex === -1) return;
    const reordered = [...visibleConfigurable];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setWorkingColumns([...fixedColumns, ...reordered, ...hiddenConfigurable]);
  };

  return (
    <Modal centered size="xl" show={show} onHide={onHide} dialogClassName="tw-max-w-[980px]">
      <Modal.Header closeButton className="tw-border-b tw-border-gray-200 tw-px-5 tw-py-3">
        <div>
          <Modal.Title className="tw-text-[30px] tw-leading-none tw-font-semibold tw-text-gray-900">Select columns for your grid.</Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body className="tw-p-0">
        <div className="tw-grid tw-grid-cols-2 tw-min-h-[430px]">
          <div className="tw-border-r tw-border-gray-200 tw-px-4 tw-py-3 tw-max-h-[430px] tw-overflow-y-auto">
            {fixedColumns.length > 0 && (
              <div className="tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <span className="tw-text-xs tw-font-medium tw-text-gray-500">FIXED COLUMNS</span>
                  <span className="tw-bg-gray-100 tw-text-gray-500 tw-rounded-md tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium">{fixedColumns.length}</span>
                </div>
                <div className="tw-space-y-2.5">
                  {fixedColumns.map((column) => (
                    <div key={column?.id} className="tw-flex tw-items-center tw-justify-between tw-p-2 tw-bg-gray-50 tw-rounded tw-border-l-2 tw-border-gray-300">
                      <span className="tw-text-sm tw-text-gray-600 tw-font-medium">{column?.display_label || column?.column_key}</span>
                      <span className="material-symbols-outlined tw-text-gray-400 tw-text-base">lock</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {groupedColumns.map((group) => (
              <div key={group.key} className="tw-mb-4">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <div className="tw-text-xs tw-font-medium tw-text-gray-500 tw-flex tw-items-center tw-gap-2">
                    {group.label}
                    <span className="tw-bg-primary-purple-100 tw-text-primary-purple-600 tw-rounded-md tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium">{group.items.length}</span>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-1.5">
                    <button className="tw-text-xs tw-font-medium tw-text-primary-purple-600 tw-bg-primary-purple-100 tw-rounded-full tw-px-2 tw-py-0.5" onClick={() => handleGroupAddAll(group.key)}>
                      Add all
                    </button>
                  </div>
                </div>

                <div className="tw-space-y-2.5">
                  {group.items.map((column) => (
                    <label key={column?.id} className={`tw-flex tw-items-center tw-justify-between w-px-2 tw-py-1.5 tw-cursor-pointer tw-p-2 ${column?.is_visible ? "tw-bg-primary-purple-50 tw-border-l-2 tw-border-primary-purple-600" : ""}`}>
                      <span className="tw-text-sm tw-text-gray-900 tw-font-medium">{column?.display_label || column?.column_key}</span>
                      <input type="checkbox" checked={column?.is_visible !== false} onChange={(event) => toggleColumn(column?.id, event.target.checked)} className="tw-h-4 tw-w-4 tw-accent-primary-purple-600 after:tw-hidden" />
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="tw-px-4 tw-py-3 tw-max-h-[430px] tw-overflow-y-auto">
            <p className="tw-text-base tw-font-medium tw-text-gray-900 tw-mb-3">{totalSelected} fields selected</p>

            {fixedColumns.length > 0 && (
              <div className="tw-mb-3">
                <p className="tw-text-xs tw-font-medium tw-text-gray-400 tw-mb-2 tw-uppercase">Fixed</p>
                <div className="tw-space-y-2">
                  {fixedColumns.map((column) => (
                    <div key={column?.id} className="tw-flex tw-items-center tw-justify-between tw-border tw-border-gray-100 tw-rounded-lg tw-px-3 tw-py-2 tw-bg-gray-50">
                      <div>
                        <p className="tw-m-0 tw-text-sm tw-text-gray-600">{column?.display_label || column?.column_key}</p>
                        <p className="tw-m-0 tw-text-xs tw-text-gray-400">Fixed Column</p>
                      </div>
                      <span className="material-symbols-outlined tw-text-gray-300">lock</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="tw-space-y-2">
              {selectedColumns.map((column) => (
                <div
                  key={column?.id}
                  draggable
                  onDragStart={() => setDraggedColumnId(column?.id ?? null)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    reorderSelectedColumns(draggedColumnId, column?.id ?? null);
                    setDraggedColumnId(null);
                  }}
                  onDragEnd={() => setDraggedColumnId(null)}
                  className={`tw-flex tw-items-center tw-justify-between tw-border tw-rounded-lg tw-px-3 tw-py-2 tw-cursor-move tw-bg-white tw-filter-none ${draggedColumnId === column?.id ? "tw-border-primary-purple-500 tw-shadow-sm !tw-opacity-100" : "tw-border-gray-200"}`}>
                  <div>
                    <p className="tw-m-0 tw-text-sm tw-text-gray-900">{column?.display_label || column?.column_key}</p>
                    <p className="tw-m-0 tw-text-xs tw-text-gray-500">{column?.column_kind === "enrichment" ? "Enrichment" : "Trade Data"}</p>
                  </div>
                  <span className="material-symbols-outlined tw-text-gray-400">drag_indicator</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="tw-border-t tw-border-gray-200 tw-px-5 tw-py-3">
        {/* <button className="tw-h-9 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-gray-700" onClick={handleResetDefault}>
          Reset
        </button> */}
        <button className="tw-h-9 tw-rounded-lg tw-border tw-border-primary-purple-500 tw-bg-primary-purple-500 tw-px-3 tw-text-sm tw-font-medium tw-text-white tw-flex tw-items-center tw-gap-1.5 hover:tw-bg-primary-purple-600 tw-shadow-button" onClick={handleSave}>
          Save Configuration
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ColumnConfigurationModal;
